import { useCallback, useEffect, useState } from 'react';
import type { ChatContext } from '@shared/api';

/**
 * Selecting text inside a proposal block arms a context chip; the next chat
 * turn carries that selection so "make this shorter" binds to the right copy.
 *
 * Blocks opt in by putting `data-section-id` / `data-section-title` on the
 * section wrapper and `data-field` on each editable text region.
 */
export type SelectionField = NonNullable<NonNullable<ChatContext['selection']>['field']>;

export interface ArmedSelection {
  sectionId: string;
  sectionTitle: string;
  field?: SelectionField;
  text: string;
}

const FIELDS: readonly string[] = ['headline', 'body', 'rationale', 'bullets', 'title'];

/** `fields.<key>` addresses one named narrative sub-field (e.g. overview's whatWeHeard). */
function isField(value: string | null | undefined): value is SelectionField {
  if (typeof value !== 'string') return false;
  return FIELDS.includes(value) || /^fields\.[^.]+$/.test(value);
}

function readSelection(): ArmedSelection | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;

  const active = document.activeElement;
  if (active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement) return null;

  const text = selection.toString().trim();
  if (text.length < 2) return null;

  const anchor = selection.anchorNode;
  const start =
    anchor instanceof Element ? anchor : (anchor?.parentElement ?? null);
  if (!start) return null;

  const sectionEl = start.closest<HTMLElement>('[data-section-id]');
  if (!sectionEl) return null;

  const sectionId = sectionEl.dataset.sectionId;
  if (!sectionId) return null;

  const fieldEl = start.closest<HTMLElement>('[data-field]');
  const rawField = fieldEl?.dataset.field;

  return {
    sectionId,
    sectionTitle: sectionEl.dataset.sectionTitle ?? sectionId,
    ...(isField(rawField) ? { field: rawField } : null),
    text,
  };
}

export interface SelectionStore {
  selection: ArmedSelection | null;
  clear: () => void;
}

export function useSelectionContext(enabled: boolean): SelectionStore {
  const [selection, setSelection] = useState<ArmedSelection | null>(null);

  const clear = useCallback(() => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let timer = 0;
    const onSelectionChange = () => {
      window.clearTimeout(timer);
      // selectionchange fires continuously during a drag; settle first.
      timer = window.setTimeout(() => {
        const next = readSelection();
        // Losing the selection (a click elsewhere) leaves the chip armed until
        // the user sends or presses Esc — otherwise clicking the composer
        // would disarm it immediately.
        if (next) setSelection(next);
      }, 140);
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !selection) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelection(null);
        window.getSelection()?.removeAllRanges();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled, selection]);

  return { selection, clear };
}
